/**
 * `aggregateParcel` — pure parcel aggregator for a single seller's items.
 *
 * Why a pure helper: the rule "1 seller_order = 1 parcel" (PLAN §J.4 + §J.7
 * locked policy A) means every checkout that routes to the live-rate sub-flow
 * needs ONE `ShippingRatesParcel` summed from its line items. Doing the math
 * inside `paypal-checkout.tsx` would put domain logic in a delivery shell;
 * the `hex` rule says it belongs in `lib/`.
 *
 * Aggregation policy:
 *  - **Weight**: simple `SUM(parcel_weight)` over (qty-respecting) items —
 *    1× 2 lb item + 1× 3 lb item = 5 lb. `qty > 1` of the same listing
 *    multiplies its weight (`weight × qty`).
 *  - **Dimensions** (L/W/H): max-per-axis. Two 10×10×10 cubes pack into a
 *    20×10×10 box on the best axis; the carrier-side DIM-weight calculation
 *    handles fine-grained dimensional pricing. PLAN §J.4 explicitly accepts
 *    this approximation for MVP (multi-parcel is V2).
 *  - **Units**: all-or-nothing. Mixed-unit cart → `null` (caller falls back
 *    to legacy mode or surfaces an error). WS3 publish-time validation pins
 *    units per-listing; cross-listing unit divergence is a data bug. Defensive
 *    null lets the UI degrade gracefully instead of computing a bogus parcel.
 *
 * Returns `null` when:
 *  - `items` is empty (no parcel to ship).
 *  - any item is missing weight or dims (publish-time WS3 invariant violation).
 *  - mass units or distance units disagree across items (unit-divergence guard).
 *
 * @see .tondocs/shippo/PLAN.md §J.4 (combined parcel — weight vs dims)
 * @see .tondocs/shippo/PLAN.md §J.7 policy A (single seller_order → single parcel)
 * @see .tondocs/skills/hex.md §1 (pure domain, no framework imports)
 * @see .tondocs/skills/type-driven-design.md §5 (Parse-Don't-Validate)
 */

import type { ShippingRatesParcel } from "@kraahl/contract";

/**
 * Minimal shape `aggregateParcel` reads from a cart-side item. Decoupled from
 * `CartItemData` so unit tests do not need to fabricate a full cart row; the
 * caller in `paypal-checkout` adapts a `CartItemData` to this shape inline.
 */
export interface ParcelInput {
  quantity: number;
  parcelWeight: number | null | undefined;
  parcelLength: number | null | undefined;
  parcelWidth: number | null | undefined;
  parcelHeight: number | null | undefined;
  massUnit: "lb" | "oz" | "kg" | "g" | null | undefined;
  distanceUnit: "in" | "cm" | "mm" | null | undefined;
}

function isPositive(n: number | null | undefined): n is number {
  return typeof n === "number" && Number.isFinite(n) && n > 0;
}

export function aggregateParcel(
  items: ReadonlyArray<ParcelInput>,
): ShippingRatesParcel | null {
  if (items.length === 0) return null;

  // Lock units off the first item; reject divergence on every subsequent one.
  // Mixed units in one checkout = data bug; defensive null is the safe degrade.
  const first = items[0]!;
  if (!first.massUnit || !first.distanceUnit) return null;
  const massUnit = first.massUnit;
  const distanceUnit = first.distanceUnit;

  let weight = 0;
  let maxLength = 0;
  let maxWidth = 0;
  let maxHeight = 0;

  for (const item of items) {
    if (item.massUnit !== massUnit) return null;
    if (item.distanceUnit !== distanceUnit) return null;
    if (!isPositive(item.parcelWeight)) return null;
    if (!isPositive(item.parcelLength)) return null;
    if (!isPositive(item.parcelWidth)) return null;
    if (!isPositive(item.parcelHeight)) return null;
    if (!Number.isFinite(item.quantity) || item.quantity <= 0) return null;

    weight += item.parcelWeight * item.quantity;
    if (item.parcelLength > maxLength) maxLength = item.parcelLength;
    if (item.parcelWidth > maxWidth) maxWidth = item.parcelWidth;
    if (item.parcelHeight > maxHeight) maxHeight = item.parcelHeight;
  }

  return {
    weight,
    length: maxLength,
    width: maxWidth,
    height: maxHeight,
    massUnit,
    distanceUnit,
  };
}
